import "./styles/Breadcrumbs.css";
import { Typography, Box } from "@mui/material";
import { ChevronRight } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useMemo, useEffect } from "react";
import notificationImage from "../../assets/notification.png";
import demoUserImage from "../../assets/demo-user.png";
import NotificationMenu from "./BreadcrumbsComponents/NotificationMenu";
import UserProfile from "./BreadcrumbsComponents/UserProfile";
import { NotificationProvider } from "./BreadcrumbsComponents/NotificationContext";
import NotificationPopup from "./BreadcrumbsComponents/NotificationPopup";
import NotificationTester from "./BreadcrumbsComponents/NotificationTester";
import notificationSound from "../../assets/sounds/notification-sound.mp3";

// Preload notification sound
const preloadAudio = src => {
  const audio = new Audio();
  audio.src = src;
  // Just loading the source, not playing
  return audio;
};

const BreadCrumbsContainer = () => {
  const location = useLocation();

  // Preload audio on component mount
  useEffect(() => {
    const audio = preloadAudio(notificationSound);

    // Force load
    audio.load();

    // Enable audio to be ready
    const enableAudio = () => {
      // Play and immediately pause to unlock audio
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            audio.pause();
            audio.currentTime = 0;
          })
          .catch(e => {
            // Ignore errors - this is just to enable audio
          });
      }
    };

    // Enable audio on user interaction
    const handleUserInteraction = () => {
      enableAudio();
      // Clean up after first interaction
      document.removeEventListener("click", handleUserInteraction);
      document.removeEventListener("keydown", handleUserInteraction);
      document.removeEventListener("touchstart", handleUserInteraction);
    };

    document.addEventListener("click", handleUserInteraction);
    document.addEventListener("keydown", handleUserInteraction);
    document.addEventListener("touchstart", handleUserInteraction);

    return () => {
      // Clean up listeners
      document.removeEventListener("click", handleUserInteraction);
      document.removeEventListener("keydown", handleUserInteraction);
      document.removeEventListener("touchstart", handleUserInteraction);
    };
  }, []);

  const breadcrumbItems = useMemo(() => {
    const path = location.pathname;
    const segments = path.split("/").filter(Boolean);

    if (segments.length === 0) {
      return [{ label: "Dashboard", path: "/" }];
    }

    // Convert path segments to readable breadcrumb items
    return segments.map((segment, index) => {
      // Format the segment to be more readable (e.g. 'addProduct' becomes 'Add Product')
      const formattedSegment = segment
        .replace(/([A-Z])/g, " $1") // Insert a space before all capital letters
        .replace(/^./, firstChar => firstChar.toUpperCase()) // Capitalize the first letter
        .replace(/([a-z])([A-Z])/g, "$1 $2") // Add space between camelCase words
        .trim();

      return {
        label: formattedSegment,
        path: "/" + segments.slice(0, index + 1).join("/"),
      };
    });
  }, [location.pathname]);

  return (
    <NotificationProvider>
      <Box
        className="breadcrumbsContainer"
        sx={{
          borderBottom: "1px solid rgba(229, 231, 235, 0.8)",
          width: "100%",
          height: "8.7vh",
          display: "flex",
          flexDirection: "row",
          backgroundColor: "#FFFFFF",
        }}>
        <Box
          className="breadcrumbsInner outfit-medium"
          sx={{
            width: "80%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            padding: "0px 24px",
          }}>
          {breadcrumbItems.map((item, index) => (
            <Box key={index} sx={{ display: "flex", alignItems: "center" }}>
              <Typography
                component="span"
                sx={{
                  color:
                    index === breadcrumbItems.length - 1
                      ? "#374151"
                      : "#6B7280",
                  fontSize: "0.875rem",
                  fontWeight: index === breadcrumbItems.length - 1 ? 600 : 500,
                  "&:hover": {
                    color:
                      index === breadcrumbItems.length - 1
                        ? "#374151"
                        : "#5652B0",
                    cursor:
                      index === breadcrumbItems.length - 1
                        ? "default"
                        : "pointer",
                  },
                }}>
                {item.label}
              </Typography>
              {index < breadcrumbItems.length - 1 && (
                <Box sx={{ display: "flex", alignItems: "center", mx: 0.5 }}>
                  <ChevronRight size={16} color="#9CA3AF" />
                </Box>
              )}
            </Box>
          ))}
        </Box>

        <Box
          className="profileNotificationContainer"
          sx={{
            width: "20%",
            height: "100%",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-evenly",
          }}>
          {/* Notification Menu */}
          <NotificationMenu notificationImage={notificationImage} />

          {/* User Profile */}
          <UserProfile
            profileImage={demoUserImage}
            userName="Demo User"
            userRole="Admin"
          />
        </Box>
      </Box>

      {/* Notification Popup */}
      <NotificationPopup />

      {/* Notification Tester for Demo */}
      <NotificationTester />
    </NotificationProvider>
  );
};

export default BreadCrumbsContainer;
